// The social layer's standard providers (decision 0054): a light affinity score per NPC (talk, gifts, deeds and
// dialogue choices move it), standing per faction (tiers with named perks, e.g. a shop discount), and NPC memory (what
// each NPC knows of the player). Each is an interface first, so an AI-integrated relationship system can replace it
// (decision 0055); the standard ones keep plain JSON state for the save.
import type { FactSource, FactValue } from "./conditions";

/** Why a score changed (for logs, an AI provider's context, and the change's own rules). */
export interface ChangeCause {
  /** "dialogue", "gift", "deed", "quest", "talk"… */
  kind: string;
  /** What exactly (a quest id, an item, an enemy kind). */
  detail?: string;
}

/** A named band of a score: its id from `min` up, with named numbers it grants (a discount, a bonus). */
export interface Tier {
  id: string;
  min: number;
  perks?: Record<string, number>;
}

/** The tier a score falls in (the highest whose `min` it reaches; the first when below all). */
export function tierOf(tiers: readonly Tier[], score: number): Tier {
  if (!tiers.length) throw new Error("tiers: none given");
  const sorted = [...tiers].sort((a, b) => a.min - b.min);
  let t = sorted[0]!;
  for (const x of sorted) if (score >= x.min) t = x;
  return t;
}

// --- affinity ------------------------------------------------------------------------------------------------------

/** One NPC's affinity rules. */
export interface AffinityNpc {
  id: string;
  start?: number;
  /** Items by how much they're liked (a gift's change comes from the provider's `gift` table). */
  gifts?: { loved?: readonly string[]; liked?: readonly string[]; disliked?: readonly string[] };
}

/** The standard affinity's rules. */
export interface AffinityRules {
  npcs: readonly AffinityNpc[];
  range: [number, number];
  /** A gift's change by how much it's liked; at most `perDay` gifts per NPC each day count. */
  gift: { loved: number; liked: number; neutral: number; disliked: number; perDay: number };
  tiers: readonly Tier[];
}

/** Affinity per NPC. */
export interface AffinityProvider {
  /** The NPCs it scores. */
  npcs(): string[];
  get(npc: string): number;
  /** Change a score by `amount` (clamped by the provider's rules); returns the new score. */
  change(npc: string, amount: number, cause: ChangeCause): number;
  /** A gift of `item` on `day`: the change it made (0 past the day's limit). */
  gift(npc: string, item: string, day: number): number;
  /** The tier of an NPC's score. */
  tier(npc: string): Tier;
  snapshot(): unknown;
  restore(data: unknown): void;
}

interface AffinityState {
  scores: Record<string, number>;
  /** Gifts counted per NPC on a day: `{ day, n }`. */
  gifts: Record<string, { day: number; n: number }>;
}

/** The standard affinity: a clamped score per NPC, gift preferences, tiers. */
export function scoreAffinity(r: AffinityRules): AffinityProvider {
  const byId = new Map(r.npcs.map((n) => [n.id, n]));
  const clamp = (v: number): number => Math.min(r.range[1], Math.max(r.range[0], v));
  const fresh = (): AffinityState => ({ scores: Object.fromEntries(r.npcs.map((n) => [n.id, clamp(n.start ?? 0)])), gifts: {} });
  let s = fresh();
  const need = (npc: string): AffinityNpc => {
    const n = byId.get(npc);
    if (!n) throw new Error(`affinity: no NPC "${npc}" (have ${[...byId.keys()].join(", ")})`);
    return n;
  };
  const p: AffinityProvider = {
    npcs: () => [...byId.keys()],
    get: (npc) => (need(npc), s.scores[npc] ?? 0),
    change(npc, amount) {
      need(npc);
      s.scores[npc] = clamp((s.scores[npc] ?? 0) + amount);
      return s.scores[npc]!;
    },
    gift(npc, item, day) {
      const n = need(npc);
      const g = s.gifts[npc]?.day === day ? s.gifts[npc]! : { day, n: 0 };
      if (g.n >= r.gift.perDay) return 0;
      g.n++;
      s.gifts[npc] = g;
      const pref = n.gifts?.loved?.includes(item) ? r.gift.loved : n.gifts?.liked?.includes(item) ? r.gift.liked : n.gifts?.disliked?.includes(item) ? r.gift.disliked : r.gift.neutral;
      const before = s.scores[npc] ?? 0;
      return p.change(npc, pref, { kind: "gift", detail: item }) - before;
    },
    tier: (npc) => tierOf(r.tiers, p.get(npc)),
    snapshot: () => structuredClone(s),
    restore(data) {
      const d = data as AffinityState;
      if (!d || typeof d.scores !== "object") throw new Error("affinity: not a score snapshot");
      s = { ...fresh(), ...structuredClone(d) };
    },
  };
  return p;
}

// --- standing ------------------------------------------------------------------------------------------------------

/** One faction's rules. */
export interface FactionDef {
  id: string;
  start?: number;
  range: [number, number];
  tiers: readonly Tier[];
}

/** Standing with each faction. */
export interface StandingProvider {
  factions(): string[];
  get(faction: string): number;
  change(faction: string, amount: number, cause: ChangeCause): number;
  tier(faction: string): Tier;
  snapshot(): unknown;
  restore(data: unknown): void;
}

/** The standard standing: a clamped score per faction with tiers and their perks. Any number of factions. */
export function tieredStanding(defs: readonly FactionDef[]): StandingProvider {
  const byId = new Map(defs.map((f) => [f.id, f]));
  const need = (id: string): FactionDef => {
    const f = byId.get(id);
    if (!f) throw new Error(`standing: no faction "${id}" (have ${[...byId.keys()].join(", ")})`);
    return f;
  };
  const clamp = (f: FactionDef, v: number): number => Math.min(f.range[1], Math.max(f.range[0], v));
  const fresh = (): Record<string, number> => Object.fromEntries(defs.map((f) => [f.id, clamp(f, f.start ?? 0)]));
  let s = fresh();
  return {
    factions: () => [...byId.keys()],
    get: (id) => (need(id), s[id] ?? 0),
    change(id, amount) {
      const f = need(id);
      s[id] = clamp(f, (s[id] ?? 0) + amount);
      return s[id]!;
    },
    tier: (id) => tierOf(need(id).tiers, s[id] ?? 0),
    snapshot: () => ({ ...s }),
    restore(data) {
      if (!data || typeof data !== "object") throw new Error("standing: not a snapshot");
      s = { ...fresh(), ...(data as Record<string, number>) };
    },
  };
}

/** The standing facts as a condition source: `standing.<faction>` (the score), `.tier` (its tier's id) and `.<perk>`
 *  (the tier's perk, 0 when it has none: `standing.town.discount`). `createStory` registers it; a game without the whole
 *  story (a shop's lab) can register it over its own provider. */
export function standingFacts(standing: StandingProvider): FactSource {
  return {
    name: "standing",
    get: (k) => {
      const [f, sub] = k.split(".", 2) as [string, string | undefined];
      if (sub === undefined) return standing.get(f);
      const t = standing.tier(f);
      return sub === "tier" ? t.id : (t.perks?.[sub] ?? 0);
    },
    knows: (k) => standing.factions().includes(k.split(".")[0]!) && k.split(".").length <= 2,
    keys: () => standing.factions(),
  };
}

// --- NPC memory ----------------------------------------------------------------------------------------------------

/** What each NPC remembers of the player: values by key (met, talks, saw.defeated…). */
export interface NpcMemory {
  get(npc: string, key: string): FactValue;
  set(npc: string, key: string, value: FactValue): void;
  /** Add to a number (missing counts as 0). Returns the new value. */
  add(npc: string, key: string, amount: number): number;
  /** Everything an NPC remembers. */
  entries(npc: string): Record<string, FactValue>;
  snapshot(): unknown;
  restore(data: unknown): void;
}

/** The standard memory: a record per NPC. */
export function mapMemory(): NpcMemory {
  let s: Record<string, Record<string, FactValue>> = {};
  const of = (npc: string): Record<string, FactValue> => (s[npc] ??= {});
  return {
    get: (npc, key) => s[npc]?.[key] ?? null,
    set: (npc, key, value) => void (of(npc)[key] = value),
    add(npc, key, amount) {
      const m = of(npc);
      const v = (typeof m[key] === "number" ? (m[key] as number) : 0) + amount;
      m[key] = v;
      return v;
    },
    entries: (npc) => ({ ...(s[npc] ?? {}) }),
    snapshot: () => structuredClone(s),
    restore(data) {
      if (!data || typeof data !== "object") throw new Error("memory: not a snapshot");
      s = structuredClone(data as typeof s);
    },
  };
}
